"use client";

import { useState } from 'react'
import Navbar from '@/components/Navbar'
import Footer from '@/components/Footer'
import { motion } from 'framer-motion'
import { Mail, Phone, MapPin, Clock, Send, Check, CheckCircle2 } from "lucide-react";
import BottomCta from '@/components/BottomCta'

const CONTACT_METHODS = [
  {
    icon: <Phone size={24} />,
    title: "Phone",
    value: (
      <>
        +971 50 798 7470
        <br />
        +971 58 600 9981
        <br />
        +971 58 600 9980
      </>
    ),
    description: ""
  },
  {
    icon: <Mail size={24} />,
    title: "Email",
    value: "info@awsbusinessmen.com",
    description: "We reply within 24 hours"
  },
  {
    icon: <MapPin size={24} />,
    title: "Office Location",
    value: "Horizon Towers, Block D",
    description: "Al Rashidiya 1, Ajman, UAE"
  },
  {
    icon: <Clock size={24} />,
    title: "Business Hours",
    value: "Mon-Sat: 9AM-9PM",
    description: "Sunday by appointment"
  }
]

const SERVICES = [
  "Business Setup",
  "Visa Services",
  "Corporate Services",
  "Accounting & Tax",
  "PRO Services",
  "Free Zone Registration"
]

const WHY_CONTACT = [
  "Expert guidance from experienced professionals",
  "Fast response times and personalized service",
  "Comprehensive solutions for all your needs",
  "Dedicated support throughout your journey",
  "Transparent communication and fair pricing"
]

export default function Contact() {
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    const data = new FormData(e.target)
    const response = await fetch("https://formspree.io/f/xbdnyapg", {
      method: "POST",
      headers: { Accept: "application/json" },
      body: data,
    })
    if (response.ok) {
      setIsSubmitted(true)
      e.target.reset()
      setTimeout(() => setIsSubmitted(false), 5000)
    }
  }

  return (
    <main className="bg-[#FAFAFA] min-h-screen w-full overflow-x-hidden text-[#000000]">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-center justify-center py-20 lg:py-32 2xl:py-48 overflow-hidden">
        <div className="max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto w-full px-6 text-center flex flex-col items-center relative z-10">

          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#B8893A]/20 bg-[#B8893A]/5 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-[#B8893A]" />
            <span className="text-[10px] sm:text-xs font-bold text-[#B8893A] uppercase tracking-[0.2em]">
              Get In Touch
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="text-4xl sm:text-5xl md:text-6xl xl:text-7xl 2xl:text-8xl font-bold tracking-tight leading-[1.15] mb-6 max-w-5xl text-[#000000]"
          >
            Let's Start Your <br />
            <span className="font-serif font-normal text-[#B8893A]">Business Journey</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="text-gray-500 text-base sm:text-lg md:text-xl xl:text-2xl max-w-2xl xl:max-w-4xl font-light leading-relaxed mb-10"
          >
            Whether you are starting a new business, setting up in a free zone, or expanding your operations in the UAE, our expert team is here to guide you every step of the way. Reach out today for personalized assistance.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto"
          >
            <a
              href="#contact-form"
              className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-white bg-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#a3762f] hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide"
            >
              Get Expert Help
            </a>
            <a
              href="https://wa.me/971507987470"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-[#B8893A] bg-transparent border-2 border-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#B8893A]/5 hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide"
            >
              Chat on WhatsApp
            </a>
          </motion.div>
        </div>
      </section>

      {/* CONTACT INFO CARDS */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-white border-y border-gray-100">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 xl:gap-8">
            {CONTACT_METHODS.map((method, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group bg-white p-8 rounded-2xl border border-gray-100/80 transition-all duration-300 hover:shadow-md text-center"
              >
                <div className="w-14 h-14 mx-auto mb-6 rounded-full bg-[#B8893A]/10 flex items-center justify-center text-[#B8893A] group-hover:bg-[#B8893A] group-hover:text-white transition-all duration-300">
                  {method.icon}
                </div>
                <h3 className="text-lg font-bold mb-2 text-black">{method.title}</h3>
                <p className="text-[#B8893A] font-semibold mb-1">{method.value}</p>
                <p className="text-gray-500 text-sm font-light">{method.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT FORM & INFO */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 xl:gap-24 items-center">
            {/* FORM */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              id="contact-form"
            >
              <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">Send Us a Message</span>
              <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">We're ready to help</h2>
              <p className="text-gray-500 font-light leading-relaxed xl:text-lg mb-8">
                Tell us about your business needs and we'll connect you with the right expert to guide you through your UAE business setup journey.
              </p>

              {isSubmitted ? (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
                  <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-green-800 mb-2">Thank You!</h3>
                  <p className="text-green-700">Your message has been received. Our team will contact you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <input
                      required
                      name="name"
                      placeholder="Full Name *"
                      className="w-full px-5 py-4 rounded-xl border border-gray-200 focus:border-[#B8893A] focus:ring-2 focus:ring-[#B8893A]/10 outline-none transition bg-white shadow-sm"
                    />
                    <input
                      required
                      type="email"
                      name="email"
                      placeholder="Email Address *"
                      className="w-full px-5 py-4 rounded-xl border border-gray-200 focus:border-[#B8893A] focus:ring-2 focus:ring-[#B8893A]/10 outline-none transition bg-white shadow-sm"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <input
                      name="phone"
                      placeholder="Phone Number"
                      className="w-full px-5 py-4 rounded-xl border border-gray-200 focus:border-[#B8893A] focus:ring-2 focus:ring-[#B8893A]/10 outline-none transition bg-white shadow-sm"
                    />
                    <select
                      name="service"
                      className="w-full px-5 py-4 rounded-xl border border-gray-200 focus:border-[#B8893A] focus:ring-2 focus:ring-[#B8893A]/10 outline-none transition bg-white shadow-sm text-gray-600"
                    >
                      <option value="">Select a Service</option>
                      {SERVICES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <textarea
                    required
                    name="message"
                    rows={5}
                    placeholder="Your Message *"
                    className="w-full px-5 py-4 rounded-xl border border-gray-200 focus:border-[#B8893A] focus:ring-2 focus:ring-[#B8893A]/10 outline-none transition bg-white shadow-sm resize-none"
                  />
                  <button
                    type="submit"
                    className="w-full py-4 rounded-xl bg-[#B8893A] text-white font-bold shadow-lg hover:bg-[#a3762f] transition-all flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <Send size={20} />
                    Send Message
                  </button>
                </form>
              )}
            </motion.div>

            {/* RIGHT SIDE - MAP & WHY CHOOSE */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {/* MAP */}
              <div className="relative h-[350px] sm:h-[450px] rounded-3xl overflow-hidden shadow-xl border border-gray-100">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3604.148003770183!2d55.4377047!3d25.3998593!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f58330768c69b%3A0xc47e3a3641775f0a!2sHorizon%20Towers%2C%20Block%20D%2C%20Office%202009%2C%20Al%20Rashidiya%201%2C%20Ajman%2C%20UAE!5e0!3m2!1sen!2sae!4v1715273000000!5m2!1sen!2sae" 
                  width="100%" 
                  height="100%" 
                  style={{ border: 0 }} 
                  allowFullScreen="" 
                  loading="lazy"
                ></iframe>
              </div>

              {/* WHY CHOOSE US */}
              <div className="bg-white border border-gray-100 rounded-2xl p-8">
                <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2 block">Why Choose Us</span>
                <h3 className="text-2xl sm:text-3xl font-black tracking-tight mb-6">Direct Access to Experts</h3>
                <div className="space-y-3">
                  {WHY_CONTACT.map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3">
                      <Check className="w-5 h-5 text-[#B8893A] shrink-0 mt-0.5" />
                      <span className="text-sm font-semibold text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <BottomCta
        title="Ready to start your UAE business?"
        subtitle="Get personalized guidance from our expert team. Whether you need business setup, visa assistance, or accounting support, we're here to make the process smooth and straightforward."
      />

      <Footer />
    </main>
  )
}
